import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  CatalogItem,
  ConfigApprovals,
  ConfigCompanyPermissions,
} from "../../app/backendTypes";
import { useContext, useMemo, useRef, useState } from "react";
import { Clear, Close } from "@mui/icons-material";
import * as Yup from "yup";
import { Field, FieldInputProps, Formik } from "formik";
import { LoadingButton } from "@mui/lab";
import Avatar from "../common/Avatar";
import { HttpClientContext, X_REQUEST_ID } from "../../app/client";
import { AxiosError } from "axios";
import Morselbar from "../common/Morselbar";
import { ObjectShape } from "yup";
import { ItemApprovals } from "../common/items/ItemApprovals";
import { ItemApprovedForCompanies } from "../common/items/ItemApprovedForCompanies";
import { ItemRestrictedForCompanies } from "../common/items/ItemRestrictedForCompanies";

export function SetCatalog(props: {
  item?: CatalogItem;
  callback: () => void;
}) {
  const { item: i, callback } = props;

  const isNew = i === undefined;
  const item = useMemo(() => {
    if (i === undefined) return undefined;

    const temp = i as unknown as {
      mim_group_id?: string | null;
      contract_id?: string | null;
    };

    if (temp.mim_group_id === null) {
      temp.mim_group_id = "";
    }

    if (temp.contract_id === null) {
      temp.contract_id = "";
    }

    return temp as unknown as CatalogItem;
  }, [i]);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState<{
    error?: string;
    content: string;
  }>({ content: "" });

  const client = useContext(HttpClientContext);

  const shape: ObjectShape = {
    id: Yup.string().uuid(),
    info: Yup.object().shape({
      display_name: Yup.string(),
      short_desc: Yup.string(),
      long_desc: Yup.string(),
      avatar: Yup.string(),
      pricing: Yup.object().shape({ desc: Yup.string() }),
      links: Yup.object().shape({ main: Yup.string().url() }),
    }),
    mim_group_id: Yup.string().uuid(),
    catalog_id: Yup.number().min(0),
  };

  if (item?.company_permissions !== undefined) {
    shape.permissions = Yup.object().shape({
      approved_ids: Yup.array().of(Yup.number().optional()),
      restricted_ids: Yup.array().of(Yup.number().optional()),
    });
  }

  if (item?.approvals !== undefined) {
    shape.approvals = Yup.object().shape({
      steps: Yup.array().of(
        Yup.object().shape({
          kind: Yup.string().oneOf(["Manager", "Dynamic", "User"]).required(),
          inner: Yup.object().when("kind", {
            is: "User",
            then: (schema) =>
              schema.shape({ id: Yup.number().not([0]).required() }),
          }),
        })
      ),
    });
  }

  const schema = Yup.object().shape(shape);

  const handleSubmit = (values: CatalogItem) => {
    setLoading(true);
    setMessageOpen(false);
    setMessage({ content: "" });

    const payload = structuredClone(values) as {
      id?: string;
      mim_group_id?: string;
      approvals?: ConfigApprovals;
      company_permissions?: ConfigCompanyPermissions;
      contract_id: string | number | null;
    };
    if (payload.id !== undefined && payload.id === "") payload.id = undefined;
    if (payload.approvals !== undefined)
      for (const step of payload.approvals.steps)
        if (step.kind !== "User") step.inner = undefined;
    if (payload.mim_group_id === "") payload.mim_group_id = undefined;
    if (payload.contract_id === "") payload.contract_id = null;
    else payload.contract_id = parseInt(payload.contract_id as string);

    client
      ?.post("/api/catalog", payload)
      .then(() => {
        setMessage({
          error: undefined,
          content: "Saved catalog item",
        });
        setMessageOpen(true);
        setOpen(false);
        callback();
      })
      .catch((e: AxiosError) => {
        setMessage({
          error: e.response?.headers[X_REQUEST_ID] || e.name,
          content: "Failed to save catalog item",
        });
        setMessageOpen(true);
      })
      .finally(() => setLoading(false));
  };

  const avatarInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        {isNew ? "Add item" : "Edit item"}
      </Button>

      <Dialog open={open} fullWidth>
        <DialogContent>
          <Formik
            initialValues={
              item !== undefined
                ? item
                : ({
                    id: "",
                    info: {
                      display_name: "",
                      short_desc: "",
                      long_desc: "",
                      mim_group_id: "",
                      avatar: "",
                      pricing: { desc: "" },
                      links: { main: "" },
                    },
                    mim_group_id: "",
                    contract_id: "",
                    approvals: {
                      steps: [],
                    },
                    company_permissions: {
                      approved_ids: [],
                      restricted_ids: [],
                    },
                  } as unknown as CatalogItem)
            }
            onSubmit={handleSubmit}
            validationSchema={schema}
          >
            {({ getFieldMeta, values, handleSubmit, setFieldValue }) => {
              return (
                <Box
                  component="form"
                  onSubmit={(...args) => {
                    handleSubmit(...args);
                  }}
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Typography variant="h5">
                      {isNew
                        ? "Create a new catalog item"
                        : "Modify existing catalog item"}
                    </Typography>
                    <IconButton onClick={() => setOpen(false)}>
                      <Close />
                    </IconButton>
                  </Box>

                  <Avatar
                    alt={values.info.display_name}
                    src={values.info.avatar}
                  />
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <input
                      disabled={loading}
                      ref={avatarInputRef}
                      type="file"
                      onChange={(e) => {
                        const reader = new FileReader();
                        const file = e.target.files?.[0];

                        if (file) {
                          reader.onload = () =>
                            setFieldValue("info.avatar", reader.result);
                          reader.readAsDataURL(file);
                        } else {
                          setFieldValue("info.avatar", "");
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFieldValue("info.avatar", "");
                        if (avatarInputRef.current) {
                          avatarInputRef.current.value = "";
                        }
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </Box>

                  <Field name="info.display_name">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.display_name");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Display Name"
                          size="small"
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="info.short_desc">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.short_desc");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Short Description"
                          size="small"
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="info.long_desc">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.long_desc");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Long Description"
                          size="small"
                          multiline
                          rows={4}
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="info.pricing.desc">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.price.desc");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Pricing"
                          size="small"
                          multiline
                          rows={2}
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="contract_id">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("contract_id");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Contract ID"
                          size="small"
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="info.links.main">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.links.main");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Link"
                          size="small"
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="mim_group_id">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("mim_group_id");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="MIM Group ID"
                          size="small"
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <br />
                  <Typography variant="h6">Approvals</Typography>
                  <ItemApprovals loading={loading} />
                  <br />

                  <br />
                  <Typography variant="h6">Approved companies</Typography>
                  <ItemApprovedForCompanies loading={loading} />
                  <br />

                  <br />
                  <Typography variant="h6">Restricted companies</Typography>
                  <ItemRestrictedForCompanies loading={loading} />
                  <br />

                  <Box display="flex" justifyContent="flex-end">
                    <LoadingButton
                      type="submit"
                      loading={loading}
                      variant="outlined"
                    >
                      Confirm
                    </LoadingButton>
                  </Box>
                </Box>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={messageOpen}
        setOpen={setMessageOpen}
        content={message.content}
        error={message.error}
      />
    </>
  );
}
